
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, ArrowLeft, Search, Edit, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MedicationForm } from "@/components/medications/MedicationForm";
import { useNavigate } from "react-router-dom";
import { MedicationConfirmation } from "@/components/medications/MedicationConfirmation";
import { MedicationAdherence } from "@/components/medications/MedicationAdherence";
import { useMedicationNotifications } from "@/hooks/use-medication-notifications";
import { MedicationEditForm } from "@/components/medications/MedicationEditForm";
import { MedicationReminderConfig } from "@/components/medications/MedicationReminderConfig";

const MobileMedications = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<any>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isReminderConfigOpen, setIsReminderConfigOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Enable real-time notifications
  useMedicationNotifications();

  const handleGoBack = () => {
    navigate("/");
  };

  const { data: medications, isLoading } = useQuery({
    queryKey: ["medications"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("medications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les médicaments",
        });
        throw error;
      }
      return data || [];
    },
  });

  const filteredMedications = medications?.filter(med => 
    med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    med.dosage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get most recent medication for the confirmation card
  const latestMedication = medications && medications.length > 0 
    ? medications[0] 
    : null;

  // Calculate adherence stats
  const adherenceStats = {
    takenPercentage: 66.7,
    onTimePercentage: 33.3,
    correctDosagePercentage: 66.7
  };

  const getExpiryStatus = (date: string | null) => {
    if (!date) return null;
    const expiryDate = new Date(date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return "expired";
    if (daysUntilExpiry <= 30) return "expiring-soon";
    return "valid";
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "expired":
        return "bg-red-100 text-red-800 border-red-200";
      case "expiring-soon":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "valid":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleEditMedication = (medication: any) => {
    setSelectedMedication(medication);
    setIsEditFormOpen(true);
  };

  const handleConfigureReminders = (medication: any) => {
    setSelectedMedication(medication);
    setIsReminderConfigOpen(true);
  };

  return (
    <div className="p-4 pb-20 mobile-content">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" onClick={handleGoBack} aria-label="Retour à l'accueil" className="mobile-button">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold">Mes médicaments</h1>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un médicament..."
            className="pl-10 mobile-button"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">
          {filteredMedications?.length || 0} médicament(s)
        </div>
        <Button size="sm" onClick={() => setIsFormOpen(true)} className="mobile-button">
          <Plus className="h-4 w-4 mr-1" /> Ajouter un médicament
        </Button>
      </div>

      <MedicationForm 
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />

      <MedicationEditForm
        medication={selectedMedication}
        open={isEditFormOpen}
        onOpenChange={setIsEditFormOpen}
      />

      <MedicationReminderConfig
        medication={selectedMedication}
        open={isReminderConfigOpen}
        onOpenChange={setIsReminderConfigOpen}
      />

      <div className="space-y-4 mb-4">
        <MedicationConfirmation medication={latestMedication} />
        <MedicationAdherence stats={adherenceStats} />
      </div>

      <div className="space-y-3 scroll-area-mobile">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => (
              <div 
                key={n} 
                className="animate-pulse h-24 rounded-lg bg-muted mobile-card"
              />
            ))}
          </div>
        ) : filteredMedications && filteredMedications.length > 0 ? (
          filteredMedications.map((medication) => {
            const expiryStatus = getExpiryStatus(medication.expiry_date);
            const statusColor = getStatusColor(expiryStatus);
            
            return (
              <div 
                key={medication.id} 
                className="p-4 border rounded-lg bg-card mobile-card shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{medication.name}</h3>
                    <p className="text-sm text-muted-foreground">{medication.dosage}</p>
                    {medication.reminder_time && medication.reminder_time.length > 0 && (
                      <div className="flex items-center mt-1 text-xs text-muted-foreground">
                        <Bell className="h-3 w-3 mr-1" />
                        <span>Rappel: {medication.reminder_time.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                      {medication.active ? "Actif" : "Inactif"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleEditMedication(medication)}
                  >
                    <Edit className="h-3 w-3 mr-1" /> Modifier
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary"
                    className="h-8 text-xs"
                    onClick={() => handleConfigureReminders(medication)}
                  >
                    <Bell className="h-3 w-3 mr-1" /> Rappels
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Aucun médicament trouvé
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMedications;
